import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import FunctiesPage from '@/pages/FunctiesPage';
import { CompanyFunctionsProvider } from '@/contexts/CompanyFunctionsContext';
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import WachtwoordVergetenPage from "@/pages/WachtwoordVergetenPage";
import WachtwoordResetPage from "@/pages/WachtwoordResetPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import DashboardPage from "@/pages/DashboardPage";
import DocListPage from "@/pages/DocListPage";
import DocumentFormPage from "@/pages/DocumentFormPage";
import KlantenPage from "@/pages/KlantenPage";
import VerkoopPage from "@/pages/VerkoopPage";
import KassaPage from "@/pages/KassaPage";
import BankPage from "@/pages/BankPage";
import VoorraadPage from "@/pages/VoorraadPage";
import InventarisPage from "@/pages/InventarisPage";
import HerinneringenPage from "@/pages/HerinneringenPage";
import BetalingsherinneringenOverzichtPage from "@/pages/BetalingsherinneringenOverzichtPage";
import HerhaaldPage from "@/pages/HerhaaldPage";
import AankopenPage from "@/pages/AankopenPage";
import BtwPage from "@/pages/BtwPage";
import BtwAangiftePage from "@/pages/BtwAangiftePage";
import BoekhoudExportPage from "@/pages/BoekhoudExportPage";
import KlantPortalPage from "@/pages/KlantPortalPage";
import BevestigBestellingPage from "@/pages/BevestigBestellingPage";
import KlantDisplayPage from "@/pages/KlantDisplayPage";
import LeverbonTekenenPage from "@/pages/LeverbonTekenenPage";
import InstellingenPage from "@/pages/InstellingenPage";
import InstellingenMockupPage from "@/pages/InstellingenMockupPage";
import BedrijfMockupPage from "@/pages/BedrijfMockupPage";
import ArtikelsPage from "@/pages/ArtikelsPage";
import ArtikelMockupPage from "@/pages/mockups/ArtikelMockupPage";
import LeverbonMobielMockupPage from "@/pages/mockups/LeverbonMobielMockupPage";
import RapportagePage from "@/pages/RapportagePage";
import GebruikersPage from "@/pages/GebruikersPage";
import AuditLogPage from "@/pages/AuditLogPage";
import BedrijvenPage from "@/pages/BedrijvenPage";
import WebhookMonitorPage from "@/pages/WebhookMonitorPage";
import InkomendeDocumentenPage from "@/pages/InkomendeDocumentenPage";
import NotificatiesPage from "@/pages/NotificatiesPage";
import AankoopBestellingenPage from "@/pages/AankoopBestellingenPage";
import InkomendeDocumentDetailPage from "@/pages/InkomendeDocumentDetailPage";
import InkomendeDocumentCockpitPage from "@/pages/InkomendeDocumentCockpitPage";
import LeveranciersPage from "@/pages/LeveranciersPage";
import VervoerdersPage from "@/pages/VervoerdersPage";
import RoutePlanningPage from "@/pages/RoutePlanningPage";
import ChauffeurRitPage from "@/pages/ChauffeurRitPage";
import VerkoperRitPage from "@/pages/VerkoperRitPage";
import VerkoopRoutePage from "@/pages/VerkoopRoutePage";
import CommissiesPage from "@/pages/CommissiesPage";
import VerkoopRapportPage from "@/pages/VerkoopRapportPage";
import KostenRapportPage from "@/pages/KostenRapportPage";
import SnelScanLeverbonPage from "@/pages/SnelScanLeverbonPage";
import DagontvangstenPage from "@/pages/DagontvangstenPage";
import TopArtikelsPage from "@/pages/TopArtikelsPage";
import TopKlantenPage from "@/pages/TopKlantenPage";
import LeverancierDetailPage from "@/pages/LeverancierDetailPage";
import ZoekReferentiePage from "@/pages/ZoekReferentiePage";
import KortingenAuditPage from "@/pages/KortingenAuditPage";
import MailHubPage from "@/pages/MailHubPage";
import ContactenPage from "@/pages/ContactenPage";
import AgendaPage from "@/pages/AgendaPage";
import AbonnementPage from "@/pages/AbonnementPage";
import WhatsAppHubPage from "@/pages/WhatsAppHubPage";
import WhatsAppMockup from "@/pages/WhatsAppMockup";
import KlantbezoekenPage from "@/pages/KlantbezoekenPage";
import '@/lib/uiPrefs';  // auto-apply user UI preferences (lettertype/grootte)

// ─── B2B Shop (klant-zijde) ───
import ShopLayout from "@/pages/shop/ShopLayout";
import ShopLoginPage from "@/pages/shop/ShopLoginPage";
import ShopSetupPage from "@/pages/shop/ShopSetupPage";
import ShopCatalogusPage from "@/pages/shop/ShopCatalogusPage";
import ShopMandjePage from "@/pages/shop/ShopMandjePage";
import ShopBestellingenPage from "@/pages/shop/ShopBestellingenPage";

function OpstartScherm() {
  const { bootError, bootPoging, retryBoot, naarLogin } = useAuth();
  return (
    <div className="h-screen flex items-center justify-center bg-[#FAFAFA] p-6" data-testid="opstart-scherm">
      <div className="text-center max-w-sm">
        <div className="mx-auto mb-4 w-10 h-10 rounded-full border-4 border-gray-200 border-t-[#002FA7] animate-spin" />
        {!bootError ? (
          <div className="text-gray-500">Laden...</div>
        ) : (
          <>
            <div className="font-bold text-gray-900">De server reageert traag</div>
            <div className="text-sm text-gray-500 mt-1">{bootError}</div>
            <div className="text-xs text-gray-400 mt-2">We proberen automatisch opnieuw… (poging {bootPoging + 1})</div>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button onClick={retryBoot} className="px-4 py-2 text-sm font-bold rounded-lg bg-[#002FA7] text-white hover:bg-[#00247f]" data-testid="opstart-opnieuw">Nu opnieuw proberen</button>
              <button onClick={naarLogin} className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50" data-testid="opstart-naar-login">Naar inloggen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <OpstartScherm />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <OpstartScherm />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <RegisterPage />} />
      <Route path="/wachtwoord-vergeten" element={<WachtwoordVergetenPage />} />
      <Route path="/wachtwoord-reset" element={<WachtwoordResetPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/portaal/:token" element={<KlantPortalPage />} />
      <Route path="/bevestig-bestelling/:token" element={<BevestigBestellingPage />} />
      <Route path="/klant-display/:code" element={<KlantDisplayPage />} />
      <Route path="/lever-tekenen/:token" element={<LeverbonTekenenPage />} />
      <Route path="/rit/:token" element={<ChauffeurRitPage />} />
      <Route path="/verkooprit/:token" element={<VerkoperRitPage />} />

      {/* ─── B2B SHOP: aparte route-tree zonder admin-Layout ─── */}
      <Route path="/shop/login" element={<ShopLoginPage />} />
      <Route path="/shop/setup/:token" element={<ShopSetupPage />} />
      <Route path="/shop" element={<ShopLayout />}>
        <Route index element={<Navigate to="/shop/bestellen" replace />} />
        <Route path="bestellen" element={<ShopCatalogusPage />} />
        <Route path="mandje" element={<ShopMandjePage />} />
        <Route path="bestellingen" element={<ShopBestellingenPage filter="orders" />} />
        <Route path="facturen" element={<ShopBestellingenPage filter="facturen" />} />
      </Route>

      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Verkopers krijgen /verkoop als landingspagina, anderen het dashboard */}
        <Route index element={
          user?.role === 'verkoper' && !user?.is_admin
            ? <Navigate to="/verkoop" replace />
            : <Navigate to="/dashboard" replace />
        } />
        <Route path="dashboard" element={
          user?.role === 'verkoper' && !user?.is_admin
            ? <Navigate to="/verkoop" replace />
            : <DashboardPage />
        } />

        {/* Document types - separate pages */}
        <Route path="facturen" element={<DocListPage docType="factuur" />} />
        <Route path="offertes" element={<DocListPage docType="offerte" />} />
        <Route path="bestelbonnen" element={<DocListPage docType="bestelbon" />} />
        <Route path="shop-bestellingen" element={<DocListPage docType="bestelbon" bronFilter="shop" titleOverride="Webshop-bestellingen" subtitleOverride="Bestelbonnen die binnenkwamen via de B2B online shop" />} />
        <Route path="leveringsbonnen" element={<DocListPage docType="leverbon" />} />
        <Route path="leveringsbonnen/snel-scan" element={<SnelScanLeverbonPage />} />
        <Route path="dagontvangsten" element={<DagontvangstenPage />} />
        <Route path="routeplanning" element={<RoutePlanningPage />} />
        <Route path="proforma" element={<DocListPage docType="proforma" />} />
        <Route path="creditnotas" element={<DocListPage docType="creditnota" />} />

        {/* Document form (shared for all types) */}
        <Route path="documenten/nieuw" element={<DocumentFormPage />} />
        <Route path="documenten/:id/bewerk" element={<DocumentFormPage />} />
        <Route path="schemas/nieuw" element={<DocumentFormPage />} />
        <Route path="schemas/:id/bewerk" element={<DocumentFormPage />} />

        <Route path="artikels" element={<ArtikelsPage />} />
        <Route path="artikels/mockup" element={<ArtikelMockupPage />} />
        <Route path="leveringsbonnen/mockup" element={<LeverbonMobielMockupPage />} />
        <Route path="kassa" element={<KassaPage />} />
        <Route path="klanten" element={<KlantenPage />} />
        <Route path="notificaties" element={<NotificatiesPage />} />
        <Route path="aankoopbestellingen" element={<AankoopBestellingenPage />} />
        <Route path="leveranciers" element={<LeveranciersPage />} />
        <Route path="leveranciers/:id" element={<LeverancierDetailPage />} />
        <Route path="vervoerders" element={<VervoerdersPage />} />
        <Route path="verkoop" element={<VerkoopPage />} />
        <Route path="commissies" element={<CommissiesPage />} />
        <Route path="verkoop-rapport" element={<VerkoopRapportPage />} />
        <Route path="kosten-rapport" element={<KostenRapportPage />} />
        <Route path="top-artikels" element={<TopArtikelsPage />} />
        <Route path="top-klanten" element={<TopKlantenPage />} />
        <Route path="kortingen-audit" element={<KortingenAuditPage />} />
        <Route path="zoek-referentie" element={<ZoekReferentiePage />} />
        <Route path="producten" element={<Navigate to="/artikels" replace />} />
        <Route path="bank" element={<BankPage />} />
        <Route path="herinneringen" element={<HerinneringenPage />} />
        <Route path="betalingsherinneringen-overzicht" element={<BetalingsherinneringenOverzichtPage />} />
        <Route path="voorraad" element={<VoorraadPage />} />
        <Route path="inventaris" element={<InventarisPage />} />
        <Route path="herhaald" element={<HerhaaldPage />} />
        <Route path="aankopen" element={<AankopenPage />} />
        <Route path="btw" element={<BtwPage />} />
        <Route path="btw-aangifte" element={<BtwAangiftePage />} />
        <Route path="boekhoudexport" element={<BoekhoudExportPage />} />
        <Route path="instellingen" element={<InstellingenPage />} />
        <Route path="instellingen/functies" element={<FunctiesPage />} />
        <Route path="super-admin" element={<SuperAdminPage />} />
        <Route path="instellingen-mockups" element={<InstellingenMockupPage />} />
        <Route path="bedrijf-mockup" element={<BedrijfMockupPage />} />
        <Route path="rapportage" element={<RapportagePage />} />
        <Route path="gebruikers" element={<GebruikersPage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
        <Route path="bedrijven" element={<BedrijvenPage />} />
        <Route path="webhook-monitor" element={<WebhookMonitorPage />} />
        <Route path="inkomende-documenten" element={<InkomendeDocumentenPage />} />
        <Route path="inkomende-documenten/:id" element={<InkomendeDocumentDetailPage />} />
        <Route path="inkomende-documenten/:id/cockpit" element={<InkomendeDocumentCockpitPage />} />
        <Route path="mail" element={<MailHubPage />} />
        <Route path="contacten" element={<ContactenPage />} />
        <Route path="agenda" element={<AgendaPage />} />
        <Route path="abonnement" element={<AbonnementPage />} />
        <Route path="whatsapp" element={<WhatsAppHubPage />} />
        <Route path="whatsapp-mockup" element={<WhatsAppMockup />} />
        <Route path="klantbezoeken" element={<KlantbezoekenPage />} />
        <Route path="klantbezoeken/route" element={<VerkoopRoutePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Globaal: komma → punt voor alle numerieke inputs (Belgisch/EU comfort)
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (!t || t.tagName !== 'INPUT') return;
      if (t.type !== 'number' && t.inputMode !== 'decimal' && t.inputMode !== 'numeric') return;
      if (e.key !== ',') return;
      e.preventDefault();
      // Voor type=number: accepteer niet altijd punt → fallback door inject
      try {
        const start = t.selectionStart;
        const end = t.selectionEnd;
        const v = t.value;
        const before = v.slice(0, start);
        const after = v.slice(end);
        if (t.type === 'number') {
          // type="number" accepteert "." als decimaal → dispatch via InputEvent
          t.focus();
          document.execCommand('insertText', false, '.');
        } else {
          t.value = before + '.' + after;
          t.setSelectionRange(start + 1, start + 1);
          t.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } catch {}
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
          <CompanyFunctionsProvider><AppRoutes /></CompanyFunctionsProvider>
          <Toaster position="top-right" richColors closeButton />
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
